import { useState, useEffect, useCallback, type ReactNode, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { Loader2, Plus, Trash2, Clock, CalendarOff } from "lucide-react";

interface AvailabilityRule {
  readonly id: string;
  readonly dayOfWeek: number;
  readonly dayName: string;
  readonly isAvailable: number;
  readonly startTime: string | null;
  readonly endTime: string | null;
}

interface Override {
  readonly id: string;
  readonly overrideDate: string;
  readonly isClosed: number;
  readonly startTime: string | null;
  readonly endTime: string | null;
  readonly reason: string | null;
}

const DAY_LABELS: Record<number, { name: string; note: string }> = {
  0: { name: "\u05D9\u05D5\u05DD \u05E8\u05D0\u05E9\u05D5\u05DF", note: "" },
  1: { name: "\u05D9\u05D5\u05DD \u05E9\u05E0\u05D9", note: "" },
  2: { name: "\u05D9\u05D5\u05DD \u05E9\u05DC\u05D9\u05E9\u05D9", note: "" },
  3: { name: "\u05D9\u05D5\u05DD \u05E8\u05D1\u05D9\u05E2\u05D9", note: "" },
  4: { name: "\u05D9\u05D5\u05DD \u05D7\u05DE\u05D9\u05E9\u05D9", note: "" },
  5: { name: "\u05D9\u05D5\u05DD \u05E9\u05D9\u05E9\u05D9", note: "\u05E2\u05E8\u05D1 \u05E9\u05D1\u05EA" },
  6: { name: "\u05E9\u05D1\u05EA", note: "\u05E9\u05D1\u05EA \u2014 \u05E1\u05D2\u05D5\u05E8" },
};

function formatDateHebrew(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function AvailabilitySettingsPage(): ReactNode {
  const { accessToken } = useAuth();
  const [rules, setRules] = useState<AvailabilityRule[]>([]);
  const [overrides, setOverrides] = useState<readonly Override[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // Override form
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideDate, setOverrideDate] = useState("");
  const [overrideClosed, setOverrideClosed] = useState(true);
  const [overrideStart, setOverrideStart] = useState("");
  const [overrideEnd, setOverrideEnd] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideSaving, setOverrideSaving] = useState(false);

  const headers = useCallback(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
  }), [accessToken]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, overridesRes] = await Promise.all([
        fetch("/api/booking/availability/rules", { headers: { Authorization: `Bearer ${accessToken}` } }),
        fetch("/api/booking/availability/overrides", { headers: { Authorization: `Bearer ${accessToken}` } }),
      ]);

      const rulesJson = (await rulesRes.json()) as { success: boolean; data: AvailabilityRule[] };
      const overridesJson = (await overridesRes.json()) as { success: boolean; data: readonly Override[] };

      if (rulesJson.success && rulesJson.data.length > 0) {
        setRules(rulesJson.data);
      } else {
        // Seed Israeli defaults
        await fetch("/api/booking/availability/rules/seed", {
          method: "POST",
          headers: headers(),
        });
        // Re-fetch
        const seededRes = await fetch("/api/booking/availability/rules", { headers: { Authorization: `Bearer ${accessToken}` } });
        const seededJson = (await seededRes.json()) as { success: boolean; data: AvailabilityRule[] };
        if (seededJson.success) setRules(seededJson.data);
      }

      if (overridesJson.success) setOverrides(overridesJson.data);
    } finally {
      setLoading(false);
    }
  }, [accessToken, headers]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleToggleDay = useCallback((dayOfWeek: number) => {
    setRules((prev) => prev.map((r) =>
      r.dayOfWeek === dayOfWeek
        ? { ...r, isAvailable: r.isAvailable ? 0 : 1 }
        : r,
    ));
    setSaved(false);
  }, []);

  const handleTimeChange = useCallback((dayOfWeek: number, field: "startTime" | "endTime", value: string) => {
    setRules((prev) => prev.map((r) =>
      r.dayOfWeek === dayOfWeek ? { ...r, [field]: value } : r,
    ));
    setSaved(false);
  }, []);

  const handleSaveRules = useCallback(async () => {
    setError("");
    setSaving(true);
    setSaved(false);

    try {
      const body = {
        rules: rules.map((r) => ({
          day_of_week: r.dayOfWeek,
          is_available: r.isAvailable,
          start_time: r.isAvailable ? r.startTime : null,
          end_time: r.isAvailable ? r.endTime : null,
        })),
      };

      const res = await fetch("/api/booking/availability/rules", {
        method: "PUT",
        headers: headers(),
        body: JSON.stringify(body),
      });

      const json = (await res.json()) as { success: boolean; error?: string };
      if (!json.success) {
        setError(json.error ?? "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DE\u05D9\u05E8\u05D4");
        return;
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("\u05E9\u05D2\u05D9\u05D0\u05EA \u05E8\u05E9\u05EA");
    } finally {
      setSaving(false);
    }
  }, [rules, headers]);

  const handleAddOverride = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setOverrideSaving(true);

    try {
      const body = {
        override_date: overrideDate,
        is_closed: overrideClosed ? 1 : 0,
        start_time: !overrideClosed && overrideStart ? overrideStart : null,
        end_time: !overrideClosed && overrideEnd ? overrideEnd : null,
        reason: overrideReason || undefined,
      };

      const res = await fetch("/api/booking/availability/overrides", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(body),
      });

      const json = (await res.json()) as { success: boolean; error?: string };
      if (json.success) {
        setShowOverrideForm(false);
        setOverrideDate("");
        setOverrideClosed(true);
        setOverrideStart("");
        setOverrideEnd("");
        setOverrideReason("");
        await fetchData();
      }
    } finally {
      setOverrideSaving(false);
    }
  }, [overrideDate, overrideClosed, overrideStart, overrideEnd, overrideReason, headers, fetchData]);

  const handleDeleteOverride = useCallback(async (id: string) => {
    await fetch(`/api/booking/availability/overrides/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    await fetchData();
  }, [accessToken, fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
          <Clock className="h-4 w-4 text-primary" />
        </div>
        <h1 className="text-lg font-bold">{"\u05E9\u05E2\u05D5\u05EA \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA"}</h1>
      </div>

      {/* Weekly hours */}
      <Card className="border-border/60 shadow-sm">
        <CardContent className="p-4 md:p-6">
          <h2 className="font-semibold mb-4 text-base">{"\u05E9\u05E2\u05D5\u05EA \u05E9\u05D1\u05D5\u05E2\u05D9\u05D5\u05EA"}</h2>

          <div className="space-y-2">
            {rules.map((rule) => {
              const dayInfo = DAY_LABELS[rule.dayOfWeek];
              const isSaturday = rule.dayOfWeek === 6;

              return (
                <div
                  key={rule.dayOfWeek}
                  className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 rounded-xl border transition-colors ${
                    isSaturday
                      ? "bg-zinc-500/10 border-zinc-500/20"
                      : rule.isAvailable
                        ? "bg-primary/5 border-primary/20"
                        : "bg-muted/20 border-border/40"
                  }`}
                >
                  {/* Day name + toggle */}
                  <div className="flex items-center gap-3 min-w-[170px]">
                    <button
                      type="button"
                      onClick={() => handleToggleDay(rule.dayOfWeek)}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
                    >
                      <div className={`w-11 h-6 rounded-full relative transition-all duration-300 ${rule.isAvailable ? "bg-primary shadow-sm shadow-primary/30" : "bg-muted"}`}>
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all duration-300 ${rule.isAvailable ? "start-6" : "start-1"}`} />
                      </div>
                    </button>
                    <div>
                      <span className={`font-medium text-sm ${rule.isAvailable ? "text-foreground" : "text-muted-foreground"}`}>{dayInfo?.name}</span>
                      {dayInfo?.note && (
                        <span className="block text-xs text-muted-foreground">{dayInfo.note}</span>
                      )}
                    </div>
                  </div>

                  {/* Time range */}
                  {rule.isAvailable ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={rule.startTime ?? "09:00"}
                        onChange={(e) => handleTimeChange(rule.dayOfWeek, "startTime", e.target.value)}
                        className="min-h-[44px] w-[120px] bg-background"
                        dir="ltr"
                      />
                      <span className="text-muted-foreground text-sm">{"\u05E2\u05D3"}</span>
                      <Input
                        type="time"
                        value={rule.endTime ?? "18:00"}
                        onChange={(e) => handleTimeChange(rule.dayOfWeek, "endTime", e.target.value)}
                        className="min-h-[44px] w-[120px] bg-background"
                        dir="ltr"
                      />
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground px-2 py-1 bg-muted/40 rounded-lg inline-block">{"\u05E1\u05D2\u05D5\u05E8"}</span>
                  )}
                </div>
              );
            })}
          </div>

          {error && <p className="text-sm text-destructive mt-3">{error}</p>}

          <div className="flex items-center gap-3 mt-5">
            <Button onClick={handleSaveRules} disabled={saving} className="min-h-[44px] shadow-sm">
              {saving && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              {"\u05E9\u05DE\u05D5\u05E8 \u05E9\u05E2\u05D5\u05EA"}
            </Button>
            {saved && (
              <span className="text-sm text-emerald-500 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                {"\u05E0\u05E9\u05DE\u05E8 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4"}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Date overrides */}
      <Card className="border-border/60 shadow-sm">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-base">{"\u05D7\u05E8\u05D9\u05D2\u05D5\u05EA \u05DE\u05D9\u05D5\u05D7\u05D3\u05D5\u05EA"}</h2>
            <Button variant="outline" onClick={() => setShowOverrideForm(!showOverrideForm)} className="min-h-[44px] hover:bg-primary/10 hover:border-primary/30 transition-colors">
              <Plus className="h-4 w-4 me-2" />
              {"\u05D4\u05D5\u05E1\u05E3 \u05D7\u05E8\u05D9\u05D2\u05D4"}
            </Button>
          </div>

          {showOverrideForm && (
            <form onSubmit={handleAddOverride} className="mb-4 p-4 border border-border/60 rounded-xl space-y-3 bg-muted/20">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{"\u05EA\u05D0\u05E8\u05D9\u05DA"}</label>
                  <Input type="date" value={overrideDate} onChange={(e) => setOverrideDate(e.target.value)} required className="min-h-[44px]" dir="ltr" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{"\u05E1\u05D9\u05D1\u05D4"}</label>
                  <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="\u05D7\u05D5\u05E4\u05E9\u05D4, \u05D7\u05D2, \u05E9\u05E2\u05D5\u05EA \u05DE\u05D9\u05D5\u05D7\u05D3\u05D5\u05EA..." className="min-h-[44px]" />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setOverrideClosed(!overrideClosed)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
                >
                  <div className={`w-11 h-6 rounded-full relative transition-all duration-300 ${overrideClosed ? "bg-destructive" : "bg-primary"}`}>
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all duration-300 ${overrideClosed ? "start-1" : "start-6"}`} />
                  </div>
                </button>
                <span className={`text-sm font-medium ${overrideClosed ? "text-destructive" : "text-primary"}`}>
                  {overrideClosed ? "\u05E1\u05D2\u05D5\u05E8 \u05DC\u05D9\u05D5\u05DD" : "\u05E4\u05EA\u05D5\u05D7 \u05D1\u05E9\u05E2\u05D5\u05EA \u05DE\u05D9\u05D5\u05D7\u05D3\u05D5\u05EA"}
                </span>
              </div>

              {!overrideClosed && (
                <div className="flex items-center gap-2">
                  <Input type="time" value={overrideStart} onChange={(e) => setOverrideStart(e.target.value)} className="min-h-[44px] w-[120px]" dir="ltr" />
                  <span className="text-muted-foreground">{"\u05E2\u05D3"}</span>
                  <Input type="time" value={overrideEnd} onChange={(e) => setOverrideEnd(e.target.value)} className="min-h-[44px] w-[120px]" dir="ltr" />
                </div>
              )}

              <div className="flex gap-2">
                <Button type="submit" disabled={overrideSaving} className="min-h-[44px]">
                  {overrideSaving && <Loader2 className="h-4 w-4 animate-spin me-2" />}
                  {"\u05D4\u05D5\u05E1\u05E3"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowOverrideForm(false)} className="min-h-[44px]">{"\u05D1\u05D9\u05D8\u05D5\u05DC"}</Button>
              </div>
            </form>
          )}

          {overrides.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <div className="w-12 h-12 rounded-xl bg-muted/50 flex items-center justify-center mx-auto mb-3">
                <CalendarOff className="h-6 w-6 opacity-40" />
              </div>
              <p className="text-sm">{"\u05D0\u05D9\u05DF \u05D7\u05E8\u05D9\u05D2\u05D5\u05EA \u05DE\u05D5\u05D2\u05D3\u05E8\u05D5\u05EA"}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {overrides.map((o) => (
                <div key={o.id} className="flex items-center justify-between p-3 border border-border/50 rounded-xl hover:bg-muted/20 transition-colors">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{formatDateHebrew(o.overrideDate)}</span>
                      <Badge
                        className={`text-xs border ${o.isClosed ? "bg-red-500/15 text-red-400 border-red-500/30" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"}`}
                      >
                        {o.isClosed ? "\u05E1\u05D2\u05D5\u05E8" : `${o.startTime}–${o.endTime}`}
                      </Badge>
                    </div>
                    {o.reason && <p className="text-xs text-muted-foreground mt-0.5">{o.reason}</p>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteOverride(o.id)} className="min-h-[44px] min-w-[44px] hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
